import React, {
  memo, useState, useEffect, useRef, useCallback,
} from 'react';
import { useDrag } from 'ahooks';
import { Popover } from 'antd';
import { handleImageUrl, getTypebyUrl } from '$/utils/image';
import IconFont from '$/components/IconFont';
import { getImageElementByURL, getVideoElementByURL } from '$/utils';
import { getVideoCoverUrl } from '$/utils/upload';
import type { RenderMarkPopover } from '$/components/InputTextArea/types';
import classNames from 'classnames';
import {
  $createTextNode,
  $createNodeSelection,
  $getNodeByKey,
  $isElementNode,
  $isTextNode,
  $setSelection,
  LexicalEditor,
  SELECTION_CHANGE_COMMAND,
} from 'lexical';
import { InlineBoxNode, INLINE_BOX_NODE_TYPE } from '$/components/InputTextArea/nodes';
import {
  getPostTaskCaretTextOffset,
  isPostTaskInlineBoxData,
  mergePostTaskAdjacentText,
} from '$/components/InputTextAreaV2/postTaskLabel';
import styles from './index.module.scss';
import {
  MAX_IMAGE_WIDTH, MAX_IMAGE_HEIGHT, calcInlineMediaDisplaySize, getInlineFileIconConfig,
} from './constants';
import {
  IInlineBox, IInlineBoxConfig, InlineBoxType, InlineBoxUpdateType, InlineBoxDragPayload,
} from './types';
import type { IInlineBoxData, MarkResult } from './types';
import MarkPopoverContent from './mod/MarkPopoverContent';
import MarkOrder from './mod/MarkOrder';
import MarkResultPopover from './mod/MarkResultPopover';

const TARGET_REGION_STYLE_INLINE_TAG = 'target_region_style';
const TARGET_REGION_STYLE_PICKER_ID = 'target_region_style_picker';
// 已选肤质同时支持 Hover 预览与点击选择，延迟预览可避免快速点击时两个浮层连续出现。
const TARGET_REGION_STYLE_HOVER_DELAY = 300;
type ClickPopoverPhase = 'idle' | 'pending' | 'open' | 'closing';
const POST_TASK_POPOVER_PLACEMENT = {
  top: 'topLeft',
  bottom: 'bottomLeft',
} as const;

/**
 * 获取行内节点用于触发点击回调的身份标识。
 * 运镜等 file 类型可能故意留空 url，此时应回退到 index。
 */
function getInlineBoxClickIdentity(
  type: InlineBoxType,
  data: IInlineBoxData,
): string | undefined {
  if (type === InlineBoxType.Mark) {
    return data.index;
  }
  return data.url || data.index;
}

/** 判断自定义 icon 是否为完整 URL，完整 URL 走图片渲染，否则按 IconFont 名称渲染。 */
function isFullUrl(value?: string) {
  if (!value) return false;
  try {
    const url = new URL(value);
    return ['http:', 'https:'].includes(url.protocol);
  } catch {
    return false;
  }
}

/**
 * Lexical 内联盒：展示图片/视频/附件/Mark 的缩略与名称，Hover 预览媒体，Mark 支持点击展开结果选择。
 */
function InlineBox({
  inlineBox,
  config,
  nodeKey,
  editor,
  clickable,
  markResultList = [],
  renderMarkPopover,
  updateMark,
  onClick,
  className,
  dragPayload,
  dragDisabled = false,
}: {
  inlineBox: IInlineBox;
  config?: IInlineBoxConfig;
  nodeKey?: string;
  editor?: LexicalEditor;
  clickable?: boolean;
  markResultList?: MarkResult[];
  className?: string;
  renderMarkPopover?: RenderMarkPopover;
  updateMark?: (params: { index: string; markInfo: Partial<IInlineBoxData>; }) => void;
  onClick?: (params: { id: string; url: string; }) => void;
  dragPayload?: InlineBoxDragPayload;
  dragDisabled?: boolean;
}) {
  const { type, data } = inlineBox;
  const mediaUrl = data.url;
  const isSelected = !!config?.[InlineBoxUpdateType.Selected];
  const isLoading = !!config?.[InlineBoxUpdateType.Loading];
  const isPostTaskLabel = isPostTaskInlineBoxData(data);
  const isTargetRegionStyleInlineBox = data.tag === TARGET_REGION_STYLE_INLINE_TAG;
  const isTargetRegionStylePicker = isTargetRegionStyleInlineBox
    && data.index === TARGET_REGION_STYLE_PICKER_ID;
  const markInlineId = inlineBox.type === InlineBoxType.Mark ? inlineBox.data.index || '' : '';
  const [selectedIndex, setSelectedIndex] = useState<number | undefined>(undefined);
  const [displaySize, setDisplaySize] = useState<{ width: number; height: number; } | null>(null);
  const [triggerBy, setTriggerBy] = useState<'hover' | 'click' | null>(null);
  const [hoverOpen, setHoverOpen] = useState(false);
  const [clickOpen, setClickOpen] = useState(false);
  const [placement, setPlacement] = useState<'top' | 'bottom'>('top');
  const popoverPlacement = isPostTaskLabel
    ? POST_TASK_POPOVER_PLACEMENT[placement]
    : placement;
  const loadUrlRef = useRef<string | null>(null);
  const clickPopoverPhaseRef = useRef<ClickPopoverPhase>('idle');
  const clickPopoverContentMountedRef = useRef(false);
  const hoverOpenTimerRef = useRef<number | null>(null);
  const pendingClickCancelTimerRef = useRef<number | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const canShowHoverPopover = data.showHoverPopover !== false
    && [InlineBoxType.Image, InlineBoxType.Video, InlineBoxType.Mark].includes(type);
  const canOpenClickPopover = Boolean(clickable && (
    type === InlineBoxType.Mark
    || (isPostTaskLabel && renderMarkPopover)
  ));
  const shouldShowClickChevron = !isLoading
    && Boolean(editor)
    && (inlineBox.type === InlineBoxType.Mark || isPostTaskLabel);
  // 用户主动选择后跳过下一次 useEffect 的自动回填，避免 editor.update 异步触发覆盖用户选择
  const skipAutoSelectRef = useRef(false);

  /** 清理等待中的肤质 Hover 预览，避免点击面板打开后延迟任务再次展示预览。 */
  const clearHoverOpenTimer = useCallback(() => {
    if (hoverOpenTimerRef.current === null) return;
    window.clearTimeout(hoverOpenTimerRef.current);
    hoverOpenTimerRef.current = null;
  }, []);

  /** 清理主键松开后的 pending 取消任务，确保随后到达的 click 可以接管状态。 */
  const clearPendingClickCancelTimer = useCallback(() => {
    if (pendingClickCancelTimerRef.current === null) return;
    window.clearTimeout(pendingClickCancelTimerRef.current);
    pendingClickCancelTimerRef.current = null;
  }, []);

  /** 主键按下后立即进入 pending 阶段，从事件源头屏蔽 Hover。 */
  const beginClickPopoverInteraction = useCallback(() => {
    clearPendingClickCancelTimer();
    if (clickPopoverPhaseRef.current !== 'open') {
      clickPopoverPhaseRef.current = 'pending';
    }
    clearHoverOpenTimer();
    setTriggerBy('click');
  }, [clearHoverOpenTimer, clearPendingClickCancelTimer]);

  /** click 未发生时撤销 pending 阶段，避免 Hover 被永久锁住。 */
  const cancelPendingClickPopover = useCallback(() => {
    if (clickPopoverPhaseRef.current !== 'pending') return;
    clickPopoverPhaseRef.current = 'idle';
    setHoverOpen(false);
    setTriggerBy(null);
  }, []);

  /** 点击内容真正卸载时结束 closing 阶段，退场 DOM 存在期间继续屏蔽 Hover。 */
  const handleClickPopoverContentRef = useCallback((element: HTMLSpanElement | null) => {
    if (element) {
      clickPopoverContentMountedRef.current = true;
      return;
    }
    if (!clickPopoverContentMountedRef.current) return;
    clickPopoverContentMountedRef.current = false;
    if (clickPopoverPhaseRef.current !== 'closing') return;
    clickPopoverPhaseRef.current = 'idle';
    setTriggerBy(null);
  }, []);

  useDrag(dragPayload ?? {}, dragPayload ? cardRef : null, {
    // eslint-disable-next-line consistent-return
    onDragStart: (event: React.DragEvent) => {
      if (dragDisabled) {
        event.preventDefault();
        return false;
      }
      try {
        if (cardRef.current) {
          event.dataTransfer.setDragImage(cardRef.current, 0, 0);
        }
      } catch (error) {
        console.warn('[InlineBox] setDragImage failed', error);
      }
      return undefined;
    },
  });

  /** 根据 type + url 异步获取媒体尺寸并计算展示宽高 */
  useEffect(() => {
    if (type !== InlineBoxType.Image && type !== InlineBoxType.Video) {
      if (loadUrlRef.current !== null) {
        loadUrlRef.current = null;
        setDisplaySize(null);
      }
      return;
    }
    const url = mediaUrl;
    if (loadUrlRef.current === url && displaySize) return;
    loadUrlRef.current = url;
    setDisplaySize(null);

    const applySize = (w: number, h: number) => {
      if (loadUrlRef.current !== url) return;
      setDisplaySize(calcInlineMediaDisplaySize(w, h));
    };
    const fallback = () => {
      if (loadUrlRef.current !== url) return;
      setDisplaySize({ width: MAX_IMAGE_WIDTH, height: MAX_IMAGE_HEIGHT });
    };

    if (type === InlineBoxType.Image) {
      getImageElementByURL(url)
        .then((img) => applySize(img.naturalWidth, img.naturalHeight))
        .catch(fallback);
    } else {
      getVideoElementByURL(url)
        .then((video) => applySize(video.videoWidth, video.videoHeight))
        .catch(fallback);
    }
  }, [displaySize, mediaUrl, type]);

  useEffect(() => {
    if (inlineBox.type !== InlineBoxType.Mark) return;
    if (skipAutoSelectRef.current) {
      skipAutoSelectRef.current = false;
      return;
    }
    let nextIndex: number | undefined;
    if (!markResultList.length) {
      nextIndex = undefined;
    } else {
      const matchIndex = markResultList.findIndex((item) => {
        if (data.objectType !== undefined && item.objectType === data.objectType) return true;
        if (data.name && item.name === data.name) return true;
        return false;
      });
      nextIndex = matchIndex >= 0 ? matchIndex : -1;
    }
    setSelectedIndex((prev) => (prev === nextIndex ? prev : nextIndex));
  }, [inlineBox.type, markResultList, data.objectType, data.name]);

  /** 点击浮层打开期间监听外部点击，并让 Hover 锁持续到退场完成。 */
  useEffect(() => {
    if (!clickOpen) return () => {};
    /** 点击浮层外部时仅关闭可见态，内容模式由退场完成回调统一清理。 */
    const onDocMouseDown = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;
      if (cardRef.current && cardRef.current.contains(target)) return;
      if (target.closest('.inlineBoxPopoverOverlay')) return;
      clearPendingClickCancelTimer();
      clickPopoverPhaseRef.current = 'closing';
      setClickOpen(false);
      setHoverOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown, true);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown, true);
    };
  }, [clearPendingClickCancelTimer, clickOpen]);

  /** mouseup 后等待同一手势的 click；失焦或 click 未发生时撤销 pending。 */
  useEffect(() => {
    const onDocumentMouseUp = () => {
      if (clickPopoverPhaseRef.current !== 'pending') return;
      clearPendingClickCancelTimer();
      pendingClickCancelTimerRef.current = window.setTimeout(() => {
        pendingClickCancelTimerRef.current = null;
        cancelPendingClickPopover();
      }, 0);
    };
    const onWindowBlur = () => {
      clearPendingClickCancelTimer();
      cancelPendingClickPopover();
    };
    document.addEventListener('mouseup', onDocumentMouseUp, true);
    window.addEventListener('blur', onWindowBlur);
    return () => {
      document.removeEventListener('mouseup', onDocumentMouseUp, true);
      window.removeEventListener('blur', onWindowBlur);
    };
  }, [cancelPendingClickPopover, clearPendingClickCancelTimer]);

  /** InlineBox 卸载时清理交互延迟任务，避免卸载后继续更新状态。 */
  useEffect(() => () => {
    clearHoverOpenTimer();
    clearPendingClickCancelTimer();
  }, [clearHoverOpenTimer, clearPendingClickCancelTimer]);

  // 点击普通 InlineBox 时切换到 NodeSelection；post_task 作为特殊富文本标签，点击主体后光标落到标签右侧。
  //
  // 关键时序问题（生产构建表现为「点两次才选中」）：
  // 1. Lexical 在 editor root 上注册了 capture 阶段的 mousedown 监听器。事件冒泡顺序
  //    是 document → root → cardRef，所以 root 上的 capture listener 比 cardRef 上的
  //    capture listener 先执行；它内部已开启 update 批，editor._updating = true。
  //    我们随后调用 editor.update 会被排进 _updates 队列，要等当前事件批 + 后续 click
  //    批全部 commit 完才轮到执行——表现为「我们的 callback 在 click:capture 之后才进入」。
  // 2. $setSelection(NodeSelection) 不会自动派发 SELECTION_CHANGE_COMMAND（该命令由
  //    native selectionchange 驱动），而 NodeSelection 不会改 native DOM selection，
  //    所以 SelectionSyncPlugin 不会刷新 __config[Selected]，CSS 选中样式不显示。
  //
  // 修复：
  // - removeAllRanges 清掉 DOM selection，避免 reconciler 在 commit 时用 native range
  //   反向覆盖我们设置的 NodeSelection；
  // - queueMicrotask 把 editor.update 推迟到当前事件批 commit 之后再执行，避免被
  //   排到队列尾部到 click 之后才跑；
  // - 显式 dispatch SELECTION_CHANGE_COMMAND 让 SelectionSyncPlugin 同步样式。
  useEffect(() => {
    const el = cardRef.current;
    if (!el || !editor || !nodeKey) return undefined;
    /** 肤质标签按下时直接切到点击内容，保持浮层连续打开并阻止 Lexical 改写光标。 */
    const onNativeMouseDown = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest('[data-post-task-delete]')) return;
      if (event.button === 0 && isTargetRegionStyleInlineBox && canOpenClickPopover) {
        beginClickPopoverInteraction();
      }
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      editor.getRootElement()?.focus({ preventScroll: true });
      try {
        window.getSelection()?.removeAllRanges();
      } catch {
        // 个别浏览器在没有可移除 range 时抛错，忽略即可
      }
      queueMicrotask(() => {
        editor.update(() => {
          if (isPostTaskLabel) {
            const postTaskNode = $getNodeByKey(nodeKey);
            const nextSibling = postTaskNode?.getNextSibling();
            if ($isTextNode(nextSibling)) {
              const offset = getPostTaskCaretTextOffset(nextSibling.getTextContent());
              nextSibling.select(offset, offset);
              return;
            }
            const parent = postTaskNode?.getParent();
            if (postTaskNode && $isElementNode(parent)) {
              const offset = postTaskNode.getIndexWithinParent() + 1;
              parent.select(offset, offset);
            }
            return;
          }
          const nodeSelection = $createNodeSelection();
          nodeSelection.add(nodeKey);
          $setSelection(nodeSelection);
        }, { discrete: true, tag: 'skip-history' });
        editor.dispatchCommand(SELECTION_CHANGE_COMMAND, undefined);
      });
    };
    el.addEventListener('mousedown', onNativeMouseDown, true);
    return () => {
      el.removeEventListener('mousedown', onNativeMouseDown, true);
    };
  }, [beginClickPopoverInteraction, canOpenClickPopover, editor,
    isPostTaskLabel, isTargetRegionStyleInlineBox, nodeKey]);

  /** 按类型渲染图标（入参 data） */
  const renderIconByType = () => {
    if (data.icon) {
      if (isFullUrl(data.icon)) {
        return <img className={styles.img} src={handleImageUrl(data.icon, { showWatermark: false, size: 152 })} alt={data.name} crossOrigin="anonymous" />;
      }
      return (
        <div className={styles['icon-box']}>
          <IconFont name={data.icon} fontSize={8} />
        </div>
      );
    }
    if ([InlineBoxType.Image, InlineBoxType.Mark].includes(type)) {
      return (
        <img className={styles.img} src={handleImageUrl(data.url, { showWatermark: false, size: 152 })} alt={data.name} crossOrigin="anonymous" />
      );
    }
    if (type === InlineBoxType.Video) {
      return (
        <>
          <img className={styles.img} src={handleImageUrl(getVideoCoverUrl(data.url), { showWatermark: false, size: 152 })} alt={data.name} crossOrigin="anonymous" />
          <IconFont name="playFill" fontSize={8} className={styles.playIcon} />

        </>
      );
    }
    if (type === InlineBoxType.File || type === InlineBoxType.Skill) {
      const { icon, background, iconColor } = getInlineFileIconConfig(data.url);
      return (
        <div className={styles['icon-box']} style={{ background }}>
          <IconFont name={icon} fontSize={8} color={iconColor} />
        </div>
      );
    }
    return null;
  };

  /** Hover 弹框：统一预览（图片/视频缩略、无控条） */
  const renderHoverContent = () => {
    const { url, name } = data;
    switch (type) {
      case InlineBoxType.Image: {
        const sizeStyle = displaySize
          ? { width: displaySize.width, height: displaySize.height }
          : { maxWidth: MAX_IMAGE_WIDTH, maxHeight: MAX_IMAGE_HEIGHT };
        return (
          <div
            className={styles['inline-box-popover']}
            style={{
              transform: placement === 'bottom' ? 'translateY(0px)' : 'translateY(12px)',
            }}
          >
            <img
              src={handleImageUrl(url, { showWatermark: false, size: 152 })}
              alt={name}
              className={styles['inline-box-popover-img']}
              style={sizeStyle}
              crossOrigin="anonymous"
            />
          </div>
        );
      }
      case InlineBoxType.Video: {
        const sizeStyle = displaySize
          ? { width: displaySize.width, height: displaySize.height }
          : { maxWidth: MAX_IMAGE_WIDTH, maxHeight: MAX_IMAGE_HEIGHT };
        return (
          <div className={styles['inline-box-popover']}>
            <video
              src={url}
              className={styles['inline-box-popover-video']}
              style={sizeStyle}
              playsInline
              autoPlay
              muted
              loop
              crossOrigin="anonymous"
            >
              <track kind="captions" />
            </video>
          </div>
        );
      }
      case InlineBoxType.Mark:
        return (
          <MarkPopoverContent
            markElementInfo={inlineBox.data}
            fitImageWidth={isTargetRegionStyleInlineBox}
          />
        );
      default:
        return null;
    }
  };

  /** 关闭点击浮层可见态；Hover 锁会保留到 antd 退场动画完成。 */
  const closePopover = () => {
    clearPendingClickCancelTimer();
    clickPopoverPhaseRef.current = 'closing';
    setClickOpen(false);
    setHoverOpen(false);
  };

  /** 更新当前 Mark 节点 data 的便捷方法（供自定义弹框使用） */
  const updateNodeData = (patch: Partial<IInlineBoxData>) => {
    if (!nodeKey || !editor) return;
    editor.update(() => {
      const node = $getNodeByKey(nodeKey);
      if (!node || node.getType() !== INLINE_BOX_NODE_TYPE) return;
      const writable = node.getWritable() as InlineBoxNode;
      writable.__inlineBox = {
        ...writable.__inlineBox,
        data: { ...writable.__inlineBox.data, ...patch },
      };
    });
  };

  /** Click 弹框：post_task 复用宿主面板，其余类型保留既有 Mark 逻辑。 */
  const renderClickContent = () => {
    if (isPostTaskLabel && renderMarkPopover && nodeKey) {
      return renderMarkPopover({
        inlineBox,
        nodeKey,
        updateData: updateNodeData,
        close: closePopover,
      });
    }

    switch (type) {
      case InlineBoxType.Mark: {
        if (renderMarkPopover && nodeKey) {
          const customContent = renderMarkPopover({
            inlineBox,
            nodeKey,
            updateData: updateNodeData,
            close: closePopover,
          });
          if (customContent !== undefined) return customContent;
        }

        return (
          <MarkResultPopover
            list={markResultList}
            width={data.width || 1}
            height={data.height || 1}
            url={data.url}
            selectedIndex={selectedIndex}
            custom={selectedIndex === -1 ? data.name : ''}
            onSelect={({ index, name }) => {
              skipAutoSelectRef.current = true;
              setSelectedIndex(index);
              if (index >= 0) {
                const item = markResultList[index];
                if (item) {
                  updateMark?.({ index: markInlineId, markInfo: { objectType: item.objectType || 0, name: item.name } });
                  updateNodeData({ name: item.name, objectType: item.objectType });
                }
              } else if (index === -1 && name) {
                updateMark?.({ index: markInlineId, markInfo: { name } });
                updateNodeData({ name, objectType: undefined });
              }
              closePopover();
            }}
          />
        );
      }
      default:
        return null;
    }
  };

  /** 按类型渲染 Popover 内容（入参 data）- 兼容旧调用，实际按 triggerBy 用上面两个 */
  const renderPopoverByType = () => {
    const isClick = clickOpen
      || clickPopoverPhaseRef.current !== 'idle'
      || triggerBy === 'click';
    if (isClick && clickable) {
      return (
        <>
          <span
            ref={handleClickPopoverContentRef}
            data-inline-box-click-popover-sentinel
            hidden
          />
          {renderClickContent()}
        </>
      );
    }
    return renderHoverContent();
  };

  /** Mark 与 post_task 标签点击时切换业务弹层，其余类型交给 onClick。 */
  const handleCardClick = () => {
    const nextOpen = clickPopoverPhaseRef.current !== 'open';
    const inlineId = getInlineBoxClickIdentity(inlineBox.type, inlineBox.data);
    if (!canOpenClickPopover) {
      if (inlineId) {
        onClick?.({ id: inlineBox.data.index || '', url: inlineBox.data.url });
      }
      return;
    }
    if (nextOpen) {
      beginClickPopoverInteraction();
      clickPopoverPhaseRef.current = 'open';
      setClickOpen(true);
      setHoverOpen(false);
    } else {
      closePopover();
    }
    if (nextOpen && cardRef.current) {
      const rect = cardRef.current.getBoundingClientRect();
      setPlacement(rect.top < 200 ? 'bottom' : 'top');
    }
    if (inlineId && nextOpen) {
      onClick?.({ id: inlineBox.data.index || '', url: inlineBox.data.url });
    }
  };

  /** 根据当前标签位置打开 Hover 预览，点击浮层已打开时保持互斥。 */
  const openHoverPopover = () => {
    if (clickPopoverPhaseRef.current !== 'idle' || !canShowHoverPopover) return;
    if (cardRef.current) {
      const rect = cardRef.current.getBoundingClientRect();
      setPlacement(rect.top < 200 ? 'bottom' : 'top');
    }
    setHoverOpen(true);
    setTriggerBy('hover');
  };

  /** 肤质标签延迟展示预览，让快速点击直接打开选择面板。 */
  const handleCardMouseEnter = () => {
    if (clickPopoverPhaseRef.current !== 'idle' || !canShowHoverPopover) return;
    clearHoverOpenTimer();
    if (isTargetRegionStyleInlineBox && !isTargetRegionStylePicker) {
      hoverOpenTimerRef.current = window.setTimeout(() => {
        hoverOpenTimerRef.current = null;
        openHoverPopover();
      }, TARGET_REGION_STYLE_HOVER_DELAY);
      return;
    }
    openHoverPopover();
  };

  /** 离开标签时同步取消等待中或已展示的 Hover 预览。 */
  const handleCardMouseLeave = () => {
    clearHoverOpenTimer();
    if (clickPopoverPhaseRef.current === 'pending') {
      clearPendingClickCancelTimer();
      cancelPendingClickPopover();
      return;
    }
    if (clickPopoverPhaseRef.current !== 'idle') return;
    setHoverOpen(false);
    if (triggerBy === 'hover') setTriggerBy(null);
  };

  /** AntD 可见态回调只处理普通浮层；肤质点击面板等待内容实际卸载再解锁。 */
  const handlePopoverAfterOpenChange = (visible: boolean) => {
    if (visible) return;
    if (clickPopoverPhaseRef.current === 'closing') {
      if (isTargetRegionStyleInlineBox) return;
      clickPopoverPhaseRef.current = 'idle';
      setTriggerBy(null);
      return;
    }
    if (clickPopoverPhaseRef.current === 'idle') setTriggerBy(null);
  };

  /** 删除 hover 中的后链路标签，清理内部锚点并把标签前后用户文本合并到同一光标位置。 */
  const handlePostTaskDelete = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    event.nativeEvent.stopImmediatePropagation?.();
    if (!editor || !nodeKey) return;

    editor.update(() => {
      const node = $getNodeByKey(nodeKey);
      if (!node) return;
      const inlineBoxNode = node as InlineBoxNode;
      if (!isPostTaskInlineBoxData(inlineBoxNode.__inlineBox?.data)) return;

      const previousSibling = node.getPreviousSibling();
      const nextSibling = node.getNextSibling();
      const leadingText = $isTextNode(previousSibling) ? previousSibling.getTextContent() : '';
      const trailingText = $isTextNode(nextSibling) ? nextSibling.getTextContent() : '';
      const leadingContent = $isTextNode(previousSibling)
        ? mergePostTaskAdjacentText(leadingText, '')
        : '';
      const mergedText = mergePostTaskAdjacentText(leadingText, trailingText);
      const caretNode = $createTextNode(mergedText);

      if ($isTextNode(previousSibling)) previousSibling.remove();
      if ($isTextNode(nextSibling)) nextSibling.remove();
      node.replace(caretNode);
      caretNode.select(leadingContent.length, leadingContent.length);
    }, { discrete: true });
    editor.dispatchCommand(SELECTION_CHANGE_COMMAND, undefined);
  };

  /** 渲染 InlineBox 卡片；肤质标签可注入一个独立的 Hover 定位锚点。 */
  const renderCard = (hoverAnchor?: React.ReactNode) => (
    <div
      ref={cardRef}
      className={classNames(styles['inline-box-card'], className, 'RoboneoChatInlineBox', {
        [styles.selected]: isSelected,
        [styles.postTaskLabel]: isPostTaskLabel,
        [styles.targetRegionStylePicker]: isTargetRegionStyleInlineBox,
      })}
      onMouseEnter={handleCardMouseEnter}
      onMouseLeave={handleCardMouseLeave}
      onMouseDown={(e) => {
        if (e.button === 0 && isTargetRegionStyleInlineBox && canOpenClickPopover) {
          beginClickPopoverInteraction();
        }
        // 阻止浏览器在 contenteditable 内重新放置 caret / 切换焦点。
        // 注意：React 的 e.stopPropagation() 只能挡住 React 合成事件，挡不住 Lexical
        // 注册在编辑器 root 上的原生 mousedown 监听器（线上构建尤其明显）。
        // 这里同时调用 nativeEvent.stopImmediatePropagation()，并通过下方 useEffect
        // 在 capture 阶段再做一次拦截，从根上避免「点两次才选中」。
        e.preventDefault();
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation?.();
      }}
      onClick={(e) => {
        e.stopPropagation();
        handleCardClick();
      }}
    >
      {hoverAnchor}
      {!isTargetRegionStylePicker ? (
        <div className={classNames(styles['inline-box-icon'], 'RoboneoChatInlineBoxIcon')}>
          {isPostTaskLabel ? (
            <>
              <span className={styles['post-task-default-icon']}>{renderIconByType()}</span>
              <button
                type="button"
                className={styles['post-task-delete-button']}
                data-post-task-delete
                aria-label="Remove preset"
                onMouseDown={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  event.nativeEvent.stopImmediatePropagation?.();
                }}
                onClick={handlePostTaskDelete}
              >
                <IconFont name="crossBold" fontSize={8} />
              </button>
            </>
          ) : renderIconByType()}
        </div>
      ) : null}
      {!isTargetRegionStyleInlineBox
          && inlineBox.type === InlineBoxType.Mark
          && data.order
          && data.showOrder !== false ? (
            <div className={styles['inline-box-name-order']}>
              <MarkOrder order={data.order} />
            </div>
        ) : null}
      <div className={styles['inline-box-name']}>
        {isLoading ? (
          <IconFont name="loadingCircleBold" fontSize={11} className={styles['inline-box-name-loading']} />
        ) : (
          <div className={styles['inline-box-name-text']}>{data.name}</div>
        )}
        {!isLoading && inlineBox.type !== InlineBoxType.Mark && (() => {
          if (data.tag === 'camera_movement') return null;
          const suffix = getTypebyUrl(data.url);
          if (!suffix) return null;
          return (
            <span className={styles['inline-box-name-suffix']}>
              .
              {suffix}
            </span>
          );
        })()}
        {shouldShowClickChevron ? (
          <IconFont
            name={clickOpen ? 'chevronUpFill' : 'chevronDownFill'}
            fontSize={isPostTaskLabel ? 8 : 6}
            className={styles['inline-box-name-suffix']}
          />
        ) : null}

      </div>
    </div>
  );

  if (isTargetRegionStyleInlineBox) {
    const hoverAnchor = canShowHoverPopover ? (
      <Popover
        overlayInnerStyle={{ padding: '0px' }}
        overlayStyle={{ pointerEvents: 'none' }}
        overlayClassName="resetPopoverAndArrow inlineBoxPopoverOverlay inlineBoxHoverPopoverOverlay"
        placement={popoverPlacement}
        autoAdjustOverflow={false}
        open={hoverOpen && clickPopoverPhaseRef.current === 'idle' && !clickOpen}
        trigger={[]}
        onOpenChange={(visible) => {
          if (!visible && clickPopoverPhaseRef.current === 'idle') {
            setHoverOpen(false);
          }
        }}
        afterOpenChange={handlePopoverAfterOpenChange}
        content={renderHoverContent()}
        zIndex={600}
      >
        <span className={styles['target-region-style-hover-anchor']} aria-hidden />
      </Popover>
    ) : null;

    return (
      <Popover
        overlayInnerStyle={{ padding: '0px' }}
        overlayClassName="resetPopoverAndArrow inlineBoxPopoverOverlay inlineBoxClickPopoverOverlay"
        placement={popoverPlacement}
        autoAdjustOverflow={false}
        open={clickOpen}
        trigger={[]}
        onOpenChange={(visible) => {
          if (visible === clickOpen) return;
          if (!visible) {
            clearPendingClickCancelTimer();
            if (clickPopoverPhaseRef.current === 'open') {
              clickPopoverPhaseRef.current = 'closing';
            }
            setClickOpen(false);
            setHoverOpen(false);
          }
        }}
        afterOpenChange={handlePopoverAfterOpenChange}
        destroyTooltipOnHide
        content={(
          <>
            <span
              ref={handleClickPopoverContentRef}
              data-inline-box-click-popover-sentinel
              hidden
            />
            {renderClickContent()}
          </>
        )}
        zIndex={600}
      >
        {renderCard(hoverAnchor)}
      </Popover>
    );
  }

  return (
    <Popover
      overlayInnerStyle={{ padding: '0px' }}
      // Hover 预览初次定位时可能短暂覆盖标签；禁用命中可避免 leave/enter 循环闪烁。
      overlayStyle={triggerBy === 'hover' ? { pointerEvents: 'none' } : undefined}
      overlayClassName="resetPopoverAndArrow inlineBoxPopoverOverlay"
      placement={popoverPlacement}
      autoAdjustOverflow={false}
      open={clickOpen || (canShowHoverPopover && hoverOpen && !clickOpen)}
      trigger={[]}
      onOpenChange={(visible) => {
        const desiredOpen = clickOpen || (hoverOpen && !clickOpen);
        if (visible === desiredOpen) return;
        if (!visible) {
          clearPendingClickCancelTimer();
          if (clickPopoverPhaseRef.current === 'open') {
            clickPopoverPhaseRef.current = 'closing';
          }
          setClickOpen(false);
          setHoverOpen(false);
        }
      }}
      afterOpenChange={handlePopoverAfterOpenChange}
      content={renderPopoverByType()}
      zIndex={600}
    >
      {renderCard()}
    </Popover>
  );
}
export default memo(InlineBox);
